import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { MdArrowBack as ArrowBackIcon } from 'react-icons/md';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useLocation } from '@tanstack/react-router';
import type { TContentBlockBase } from '@onesource/schemas';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set PDF.js worker for react-pdf v10+ (using CDN for compatibility with Vite)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface LocationState {
  block?: TContentBlockBase;
  lessonId?: string;
}

/**
 * Format file size to human-readable format
 */
const formatFileSize = (bytes: number | null): string | null => {
  if (!bytes) {
    return null;
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

/**
 * Resource Viewer Page
 * Full-page PDF viewer for resource content blocks
 */
export const ResourceViewerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;

  const block = state?.block;

  // PDF viewer state
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.2);
  const [loading, setLoading] = useState<boolean>(true);

  // PDF navigation and zoom
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));

  // Loading state if no block data
  if (!block) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Resource data not available. Please navigate from a lesson.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })}>
          Back to Community Hub
        </Button>
      </Box>
    );
  }

  // Check if it's a valid PDF block
  if (!block.pdfUrl) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          This content block does not have a PDF resource.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 3 }} maxWidth="1200px" mx="auto">
      {/* Resource Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          {block.title || 'Resource'}
        </Typography>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          {block.pdfFilename && (
            <Typography variant="body2" color="text.secondary">
              {block.pdfFilename}
            </Typography>
          )}
          {block.pdfFileSize && <Chip label={formatFileSize(block.pdfFileSize)} size="small" variant="outlined" />}
          {block.pdfPageCount && <Chip label={`${block.pdfPageCount} pages`} size="small" variant="outlined" />}
          <Button
            size="small"
            variant="contained"
            startIcon={<DownloadIcon />}
            href={block.pdfUrl}
            download={block.pdfFilename || 'document.pdf'}
          >
            Download
          </Button>
        </Box>
      </Box>

      {/* Description */}
      {block.body && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            {block.body}
          </Typography>
        </Box>
      )}

      {/* PDF Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 2,
          p: 2,
          backgroundColor: 'grey.100',
          borderRadius: 2,
        }}
      >
        <IconButton onClick={goToPrevPage} disabled={pageNumber <= 1}>
          <NavigateBeforeIcon />
        </IconButton>
        <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'center' }}>
          Page {pageNumber} of {numPages || block.pdfPageCount || '?'}
        </Typography>
        <IconButton onClick={goToNextPage} disabled={pageNumber >= numPages}>
          <NavigateNextIcon />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <IconButton onClick={zoomOut} disabled={scale <= 0.5}>
          <ZoomOutIcon />
        </IconButton>
        <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
          {Math.round(scale * 100)}%
        </Typography>
        <IconButton onClick={zoomIn} disabled={scale >= 2.5}>
          <ZoomInIcon />
        </IconButton>
      </Box>

      {/* PDF Viewer */}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'auto',
          maxHeight: 'calc(100vh - 400px)',
          minHeight: 500,
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: 'grey.200',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        <Document file={block.pdfUrl} onLoadSuccess={onDocumentLoadSuccess} loading={null}>
          <Page pageNumber={pageNumber} scale={scale} renderTextLayer renderAnnotationLayer />
        </Document>
      </Box>

      {/* Bottom Navigation */}
      <Box pt={3} display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          href={block.pdfUrl}
          download={block.pdfFilename || 'document.pdf'}
        >
          Download PDF
        </Button>
      </Box>
    </Box>
  );
};
